import { extractTextShapeContent } from './canvasTextContent.js';

function activityId(activity) {
  return String(activity?._id || activity?.actionId || '');
}

function activityTitle(activity) {
  if (!activity) return '';
  const url = activity?.content?.[0]?.urls?.[0];
  return String(url?.title || activity?.content?.[0]?.text || '').trim();
}

export function truncateContextLabel(text, max = 40) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, Math.max(1, max - 1))}…`;
}

export function formatContextChipLabel(kind, title, max = 40) {
  const text = String(title || '').trim() || 'Untitled';
  if (kind === 'ai-pin') {
    return truncateContextLabel(`AI · ${text}`, max);
  }
  if (kind === 'user-pin') {
    return truncateContextLabel(`Pin · ${text}`, max);
  }
  return truncateContextLabel(text, max);
}

function buildActivityLabelMap(activities = []) {
  const map = new Map();
  for (const activity of activities) {
    const id = activityId(activity);
    if (!id) continue;
    const title = activityTitle(activity) || id.slice(-6);
    map.set(id, formatContextChipLabel('user-pin', title));
  }
  return map;
}

export function selectionItemFromShape(shape, { activities = [], pinLabelById = null } = {}) {
  if (!shape) return null;

  const labels = pinLabelById || buildActivityLabelMap(activities);

  if (shape.type === 'pin-card' && shape.meta?.activityId) {
    const id = String(shape.meta.activityId);
    const activity = activities.find(item => activityId(item) === id);
    const title = activityTitle(activity) || id.slice(-6);
    return {
      key: `user-pin:${id}`,
      kind: 'user-pin',
      type: 'pin',
      id,
      title,
      label: labels.get(id) || formatContextChipLabel('user-pin', title),
    };
  }

  if (shape.type === 'suggest-pin') {
    const id = String(shape.id);
    const title = String(shape.props?.title || shape.props?.url || 'Suggested pin').trim();
    return {
      key: `ai-pin:${id}`,
      kind: 'ai-pin',
      type: 'shape',
      id,
      title,
      label: formatContextChipLabel('ai-pin', title),
    };
  }

  if (shape.type === 'conversation-pin') {
    const id = String(shape.id);
    const title = String(shape.props?.title || 'Board AI chat').trim();
    const kindLabel = shape.props?.pinKind === 'exchange' ? 'Q&A' : 'Chat';
    return {
      key: `conversation-pin:${id}`,
      kind: 'ai-pin',
      type: 'shape',
      id,
      title,
      label: formatContextChipLabel('ai-pin', `${kindLabel} · ${title}`),
    };
  }

  const id = String(shape.id);
  let title = shape.type || 'Shape';
  if (shape.type === 'frame') {
    title = shape.props?.name || 'Frame';
  } else if (shape.type === 'text' || shape.type === 'note') {
    title = extractTextShapeContent(shape).slice(0, 80) || 'Text';
  } else if (shape.type === 'geo') {
    title = shape.props?.geo || 'Shape';
  } else if (shape.type === 'arrow') {
    title = 'Connect line';
  } else if (shape.type === 'image') {
    title = 'Image';
  } else if (shape.type === 'draw') {
    title = 'Drawing';
  }

  return {
    key: `shape:${id}`,
    kind: 'shape',
    type: 'shape',
    id,
    title: String(title),
    label: formatContextChipLabel('shape', title),
  };
}
